import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Download, PenLine } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What should a copywriter invoice include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A copywriter invoice should include your name and contact details, the client name and project or campaign name, a unique invoice number and date, payment due date, a description of the work (content type, word count or deliverables), your rate (per word, per piece, or project fee), any revision or rush charges, applicable tax, and your payment details.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do copywriters charge — per word, per project, or hourly?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'All three are common. Per-word rates suit content writing (blog posts, articles). Per-project fees suit defined deliverables like ad copy, landing pages, or email sequences where the scope is clear. Hourly rates suit strategic work like brand voice development or content audits. Many copywriters combine methods — a fixed fee for a campaign with hourly billing for strategy calls.',
      },
    },
    {
      '@type': 'Question',
      name: 'Should I include a rush fee on my copywriting invoice?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes, if the client requested a turnaround faster than your standard timeline. Add a "Rush delivery fee" as a separate line item. Many copywriters charge 25–50% on top of the project fee for same-day or next-day delivery. Make sure your pricing policy is stated in your contract so the client expects the charge.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do I invoice for ongoing content retainers?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'For retainer agreements, invoice at the start of each month for the agreed deliverable set — for example, "Monthly content retainer: 4 blog posts, 8 social captions — May 2025." List the retainer fee as a single line item or break it down by content type. Send the invoice on the same date each month to build a predictable billing rhythm.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is this copywriter invoice template free?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic provides a free copywriter invoice template. Fill in your project details online and download a professional PDF instantly. Free for up to 5 invoices per month with no sign-up required.',
      },
    },
  ],
};

export default function CopywriterInvoiceTemplatePage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Copywriter Invoice Template UK: Free PDF for Freelance Copywriters | Invilic"
        description="Free copywriter invoice template for UK and international freelance copywriters. Bill per word, per project, or retainer. HMRC-ready, instant PDF. No sign-up needed."
        canonical="https://www.invilic.com/copywriter-invoice-template"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <span className="inline-block bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            Free Template
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Copywriter Invoice Template
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            A professional invoice template for copywriters and content writers. Bill for projects, retainers, and rush work. Download a clean PDF in seconds — no account needed.
          </p>
          <Link
            to="/create/freelancer"
            className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-indigo-600/20 transition-all duration-200 text-lg"
          >
            Use Free Template <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            Built for Every Type of Writer
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { title: 'Advertising & brand copywriters', desc: 'Bill for ad copy, taglines, brand voice guides, and campaign content by project or day rate.' },
              { title: 'Content writers & bloggers', desc: 'Invoice for blog posts, articles, and web content by the word, piece, or monthly retainer.' },
              { title: 'Email & direct response writers', desc: 'Bill for email sequences, sales pages, and direct mail copy with clear per-project fees.' },
              { title: 'UX & product writers', desc: 'Issue invoices for interface copy, onboarding flows, and product messaging work.' },
            ].map(({ title, desc }) => (
              <div key={title} className="flex gap-4 p-5 rounded-xl border border-gray-100 dark:border-gray-800">
                <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center flex-shrink-0">
                  <PenLine className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white text-sm mb-1">{title}</div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            What's in the Copywriter Invoice Template
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              'Your name and contact details',
              'Client name and campaign / project name',
              'Invoice number and date',
              'Payment due date',
              'Content type and description',
              'Word count or deliverable quantity',
              'Rate per word, per piece, or project fee',
              'Rush delivery fee (if applicable)',
              'Additional revision charges',
              'Subtotal, tax and discount',
              'Total amount due',
              'Bank details or payment link',
            ].map(f => (
              <div key={f} className="flex items-center gap-3 p-4 rounded-xl border border-indigo-100 dark:border-indigo-800/30 bg-indigo-50 dark:bg-indigo-900/10">
                <CheckCircle className="w-4 h-4 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300">{f}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            How to Create Your Copywriting Invoice
          </h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { n: '1', title: 'Open the template', desc: 'Click "Use Free Template" above. No software to install, no account needed.' },
              { n: '2', title: 'Add your project details', desc: 'Enter your work, deliverables, rate, and any rush or revision charges.' },
              { n: '3', title: 'Download PDF', desc: 'Download a professional PDF invoice ready to send to your client.' },
            ].map(({ n, title, desc }) => (
              <div key={n} className="text-center p-6 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
                <div className="w-10 h-10 rounded-full bg-indigo-600 text-white font-bold text-lg flex items-center justify-center mx-auto mb-4">
                  {n}
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            Copywriter Invoice Template: FAQs
          </h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {acceptedAnswer.text}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* UK copywriter section */}
        <section className="mb-16 p-6 rounded-2xl border border-indigo-100 dark:border-indigo-800/40 bg-indigo-50 dark:bg-indigo-900/20">
          <h2 className="font-bold text-gray-900 dark:text-white mb-3">Freelance Copywriter Invoicing in the UK</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            UK-based freelance copywriters operating as sole traders need to understand these rules:
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { label: '£1,000 trading allowance', detail: 'If your copywriting income is under £1,000/year, you may be covered by the HMRC trading allowance and may not need to register for Self Assessment.' },
              { label: 'Self Assessment above £1,000', detail: 'Register for HMRC Self Assessment by 5 October following the tax year in which you exceeded the threshold. File your return by 31 January.' },
              { label: 'VAT threshold: £90,000', detail: 'Copywriting services are standard-rated at 20% VAT. You register once turnover exceeds £90,000 in any 12-month period.' },
              { label: 'Invoice numbering for HMRC', detail: 'Use a sequential invoice number (INV-2026-001) — HMRC may review your invoice records during an audit and expects no gaps in the sequence.' },
            ].map(({ label, detail }) => (
              <div key={label} className="p-3 bg-white dark:bg-gray-900/50 rounded-xl border border-indigo-100 dark:border-indigo-800/30">
                <p className="font-semibold text-gray-900 dark:text-white text-xs mb-1">{label}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{detail}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Create Your Copywriting Invoice Now for Free
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF. No account required. Ready in 2 minutes.</p>
          <Link
            to="/create/freelancer"
            className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-indigo-600/20 transition-all duration-200 text-lg"
          >
            <Download className="w-5 h-5" /> Get Free Copywriter Invoice Template
          </Link>
        </section>
      </main>
    </div>
  );
}
